/**
 * 目次のスクロール連動・クリックスクロール・「トップに戻る」（05_ui.md §6.1 / 04_sequence.md §4）。
 */

const HEADER_OFFSET = 66;

function prefersReducedMotion(): boolean {
  return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
}

function scrollBehavior(): ScrollBehavior {
  return prefersReducedMotion() ? "auto" : "smooth";
}

/** 文書順の見出しのうち、ビューポート上端（ヘッダー分を除く）を通過した最後のものを返す */
function lastPassedHeading(headings: Element[]): Element | null {
  let last: Element | null = null;
  for (const heading of headings) {
    if (heading.getBoundingClientRect().top < HEADER_OFFSET) {
      last = heading;
    }
  }
  return last;
}

/**
 * 目次クリックの対象見出しが、非表示のタブパネル（`:::tabs`）やクイズの回答
 * （`:::quiz`）の中にある場合、祖先を辿って表示状態にする（High-2 対応。spec.md
 * 「項目クリックで該当位置へスクロールする」を実利用時にも成立させる）。
 *
 * Accordion 内の見出しは目次対象外（08_directive-syntax.md）のためここでは扱わない。
 *
 * タブ / クイズそれぞれの表示状態の切り替えは、既存の `content/tabs.ts` /
 * `content/quiz.ts` が登録済みのクリックハンドラを合成イベントで呼び出すことで再利用する
 * （design.md D4: data 属性駆動の共通初期化を維持し、状態遷移ロジックを重複させない）。
 */
function revealAncestors(target: HTMLElement): void {
  let el: HTMLElement | null = target;
  while (el) {
    if (el.hasAttribute("data-lms-tabpanel") && el.hidden) {
      const index = el.dataset["lmsTabpanel"];
      const tabsRoot = el.closest("[data-lms-tabs]");
      const tabButton =
        index !== undefined
          ? tabsRoot?.querySelector<HTMLButtonElement>(`[data-lms-tab="${index}"]`)
          : null;
      tabButton?.click();
    } else if (el.hasAttribute("data-lms-quiz-answer") && el.hidden) {
      const quizRoot = el.closest("[data-lms-quiz]");
      const toggle = quizRoot?.querySelector<HTMLButtonElement>("[data-lms-quiz-toggle]");
      toggle?.click();
    }
    el = el.parentElement;
  }
}

function initSingle(root: HTMLElement): void {
  if (root.dataset["lmsTocReady"] === "1") return;
  root.dataset["lmsTocReady"] = "1";

  const list = root.querySelector<HTMLElement>("[data-lms-toc-list]");
  const links = Array.from(root.querySelectorAll<HTMLAnchorElement>("[data-lms-toc-link]"));
  const topButton = root.querySelector<HTMLButtonElement>("[data-lms-toc-top]");

  const linkByHeading = new Map<Element, HTMLAnchorElement>();
  const headings: Element[] = [];
  for (const link of links) {
    const id = link.dataset["lmsTocLink"];
    if (!id) continue;
    const target = document.getElementById(id);
    if (target) {
      linkByHeading.set(target, link);
      headings.push(target);
    }
  }

  let activeLink: HTMLAnchorElement | null = null;

  function setActive(link: HTMLAnchorElement | null): void {
    if (activeLink === link) return;
    if (activeLink) {
      activeLink.classList.remove("is-active");
      activeLink.removeAttribute("aria-current");
    }
    if (link) {
      link.classList.add("is-active");
      link.setAttribute("aria-current", "true");
      keepVisibleInList(link);
    }
    activeLink = link;
  }

  function keepVisibleInList(link: HTMLAnchorElement): void {
    if (!list) return;
    const listRect = list.getBoundingClientRect();
    const linkRect = link.getBoundingClientRect();
    if (linkRect.top < listRect.top) {
      list.scrollTop -= listRect.top - linkRect.top;
    } else if (linkRect.bottom > listRect.bottom) {
      list.scrollTop += linkRect.bottom - listRect.bottom;
    }
  }

  function activateHeading(heading: Element | null): void {
    setActive(heading ? (linkByHeading.get(heading) ?? null) : null);
  }

  if (headings.length > 0 && typeof IntersectionObserver !== "undefined") {
    const observer = new IntersectionObserver(
      (entries) => {
        const intersecting = new Set(entries.filter((e) => e.isIntersecting).map((e) => e.target));
        let chosen: Element | null = null;
        for (const heading of headings) {
          if (intersecting.has(heading)) {
            chosen = heading;
            break;
          }
        }
        activateHeading(chosen ?? lastPassedHeading(headings));
      },
      { rootMargin: "-66px 0px -70% 0px", threshold: 0 },
    );
    headings.forEach((heading) => observer.observe(heading));
  } else if (headings.length > 0) {
    let ticking = false;
    const handleScroll = (): void => {
      if (ticking) return;
      ticking = true;
      window.setTimeout(() => {
        activateHeading(lastPassedHeading(headings));
        ticking = false;
      }, 16);
    };
    window.addEventListener("scroll", handleScroll, { passive: true });
    handleScroll();
  }

  for (const link of links) {
    link.addEventListener("click", (e) => {
      const id = link.dataset["lmsTocLink"];
      if (!id) return;
      const target = document.getElementById(id);
      if (!target) return;
      e.preventDefault();
      revealAncestors(target);
      target.scrollIntoView({ behavior: scrollBehavior(), block: "start" });
      history.replaceState(null, "", "#" + id);
    });
  }

  if (topButton) {
    topButton.addEventListener("click", () => {
      window.scrollTo({ top: 0, behavior: scrollBehavior() });
    });
  }
}

export function initToc(scope: ParentNode = document): void {
  scope.querySelectorAll<HTMLElement>("[data-lms-toc]").forEach((root) => {
    initSingle(root);
  });
}
